// pages/Orders/OrderTracking.js
import React, { useState, useEffect } from 'react';
import './OrderTracking.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';

const OrderTracking = ({ orderNumber, setCurrentPage }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchOrderNumber, setSearchOrderNumber] = useState(orderNumber || '');

  useEffect(() => {
    if (orderNumber) {
      fetchOrderDetails(orderNumber);
    } else {
      setLoading(false);
    }
  }, [orderNumber]);

  const fetchOrderDetails = async (orderNum) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderNum}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setOrder(data.data.order);
      } else {
        setError(data.message || 'Order not found');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchOrderNumber.trim()) {
      fetchOrderDetails(searchOrderNumber.trim());
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return `$${parseFloat(price || 0).toFixed(2)}`;
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return classes[status?.toLowerCase()] || 'status-pending';
  };

  const getInstallationStatusClass = (status) => {
    const classes = {
      not_required: 'install-not-required',
      pending: 'install-pending',
      scheduled: 'install-scheduled',
      in_progress: 'install-in-progress',
      completed: 'install-completed',
      cancelled: 'install-cancelled'
    };
    return classes[status?.toLowerCase()] || 'install-pending';
  };

  const renderOrderTimeline = () => {
    const statuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
    const currentStatus = order.status.toLowerCase();
    const currentIndex = statuses.indexOf(currentStatus);

    return (
      <div className="order-timeline">
        {statuses.map((status, index) => {
          const isActive = index <= currentIndex;
          const isCurrent = status === currentStatus;
          
          return (
            <div 
              key={status} 
              className={`timeline-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
            >
              <div className="timeline-dot"></div>
              <div className="timeline-label">{status}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderInstallationDetails = () => {
    if (!order.installation || !order.installation.requires_installation) {
      return null;
    }

    const install = order.installation;

    return (
      <div className="installation-section">
        <h2>📦 Installation Service</h2>
        
        <div className="installation-card">
          <div className="install-status-header">
            <span className={`install-status-badge ${getInstallationStatusClass(install.installation_status)}`}>
              {install.installation_status?.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          {install.installation_status === 'scheduled' && (
            <div className="install-scheduled-info">
              <div className="install-info-row">
                <span className="install-icon">📅</span>
                <div>
                  <strong>Installation Date</strong>
                  <p>{formatDate(install.installation_date)}</p>
                </div>
              </div>

              <div className="install-info-row">
                <span className="install-icon">⏰</span>
                <div>
                  <strong>Time Slot</strong>
                  <p>{install.installation_time_slot_label}</p>
                </div>
              </div>

              {install.technician?.name && (
                <div className="install-info-row">
                  <span className="install-icon">👨‍🔧</span>
                  <div>
                    <strong>Technician</strong>
                    <p>{install.technician.name}</p>
                    {install.technician.phone && (
                      <p className="technician-phone">{install.technician.phone}</p>
                    )}
                  </div>
                </div>
              )}

              {install.installation_notes && (
                <div className="install-notes">
                  <strong>Installation Notes:</strong>
                  <p>{install.installation_notes}</p>
                </div>
              )}
            </div>
          )}

          {install.installation_status === 'pending' && (
            <div className="install-pending-info">
              <p>⏳ Your installation is pending. Our team will contact you soon to schedule a convenient time.</p>
            </div>
          )}

          {install.installation_status === 'completed' && (
            <div className="install-completed-info">
              <p>✅ Installation completed on {formatDate(install.installation_completed_at)}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="order-tracking-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-tracking-page">
        <div className="tracking-header">
          <h1>Track Your Order</h1>
          <p>Enter your order number to track your order</p>
        </div>

        <div className="tracking-search-section">
          <form onSubmit={handleSearch} className="tracking-search-form">
            <input
              type="text"
              placeholder="Enter Order Number (e.g., ORD-20250101-ABC123)"
              value={searchOrderNumber}
              onChange={(e) => setSearchOrderNumber(e.target.value)}
              className="tracking-search-input"
            />
            <button type="submit" className="btn btn-primary">
              Track Order
            </button>
          </form>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="tracking-help">
          <h3>Need Help?</h3>
          <p>Your order number was sent to your email after placing the order.</p>
          <button 
            className="btn btn-secondary"
            onClick={() => setCurrentPage('my-orders')}
          >
            View My Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-tracking-page">
      <div className="tracking-header">
        <h1>Order Tracking</h1>
        <p>Order #{order.order_number}</p>
      </div>

      {/* Order Status Timeline */}
      <div className="tracking-section">
        <h2>Order Status</h2>
        {renderOrderTimeline()}
        
        <div className="current-status-info">
          <span className={`status-badge ${getStatusClass(order.status)}`}>
            {order.status}
          </span>
          <p className="status-description">
            {order.status === 'pending' && 'Your order has been received and is being processed.'}
            {order.status === 'confirmed' && 'Your order has been confirmed and is being prepared.'}
            {order.status === 'processing' && 'Your order is being processed and will be shipped soon.'}
            {order.status === 'shipped' && 'Your order has been shipped and is on its way.'}
            {order.status === 'delivered' && 'Your order has been delivered successfully.'}
            {order.status === 'cancelled' && 'Your order has been cancelled.'}
          </p>
        </div>
      </div>

      {/* Delivery Information */}
      {order.installation?.estimated_delivery_date && (
        <div className="tracking-section delivery-info-section">
          <h2>📦 Delivery Information</h2>
          <div className="delivery-info-card">
            <div className="delivery-date">
              <strong>Estimated Delivery:</strong>
              <span>{formatDate(order.installation.estimated_delivery_date)}</span>
            </div>
            {order.installation.actual_delivery_date && (
              <div className="delivery-date">
                <strong>Actual Delivery:</strong>
                <span>{formatDate(order.installation.actual_delivery_date)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Installation Details */}
      {renderInstallationDetails()}

      {/* Order Details */}
      <div className="tracking-section">
        <h2>Order Details</h2>
        <div className="order-details-grid">
          <div className="detail-item">
            <strong>Order Date:</strong>
            <span>{formatDate(order.created_at)}</span>
          </div>
          <div className="detail-item">
            <strong>Total Amount:</strong>
            <span>{formatPrice(order.total_amount)}</span>
          </div>
          <div className="detail-item">
            <strong>Payment Method:</strong>
            <span>{order.payment_method?.toUpperCase() || 'COD'}</span>
          </div>
          <div className="detail-item">
            <strong>Payment Status:</strong>
            <span>{order.payment_status || 'Pending'}</span>
          </div>
        </div>
      </div>

      {/* Shipping Address */}
      <div className="tracking-section">
        <h2>Shipping Address</h2>
        <div className="address-card">
          <p><strong>{order.customer_name}</strong></p>
          <p>{order.shipping_address.address}</p>
          <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}</p>
          <p>{order.shipping_address.country}</p>
          <p>Phone: {order.customer_phone}</p>
        </div>
      </div>

      {/* Order Items */}
      <div className="tracking-section">
        <h2>Order Items</h2>
        <div className="order-items-list">
          {order.items?.map((item, index) => (
            <div key={index} className="order-item-row">
              {item.image && (
                <img src={item.image} alt={item.title} className="item-image" />
              )}
              <div className="item-details">
                <strong>{item.title}</strong>
                <p>Quantity: {item.quantity}</p>
              </div>
              <div className="item-price">
                {formatPrice(item.total_price)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="tracking-actions">
        <button 
          className="btn btn-secondary"
          onClick={() => setCurrentPage('home')}
        >
          Continue Shopping
        </button>
        <button 
          className="btn btn-secondary"
          onClick={() => window.print()}
        >
          Print Details
        </button>
      </div>
    </div>
  );
};

export default OrderTracking;